import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  UserPlus,
  UserMinus,
  Search,
  RefreshCw,
  ChevronRight,
  CheckSquare,
  Square,
} from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Badge } from '@shared/components/ui/badge';
import { Skeleton } from '@shared/components/ui/skeleton';
import { Checkbox } from '@shared/components/ui/checkbox';
import { ScrollArea } from '@shared/components/ui/scroll-area';
import { Separator } from '@shared/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import { useClientAssignments } from '@features/client-support/hooks/useClientSupport';
import HeadLayout from '@shared/layouts/HeadLayout';

const ClientSupportManagement = () => {
  const { t } = useTranslation();
  const [selectedSupportId, setSelectedSupportId] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);

  const {
    supportUsers,
    assignedClients,
    unassignedClients,
    isLoadingSupportUsers,
    isLoadingAssignedClients,
    isLoadingUnassignedClients,
    assignClients,
    unassignClients,
    isAssigning,
    isUnassigning,
    refetchSupportUsers,
    refetchAssignedClients,
    refetchUnassignedClients,
  } = useClientAssignments(selectedSupportId);

  const handleSelectSupport = (supportId) => {
    setSelectedSupportId(supportId);
    setSelectedClients([]);
  };

  const handleToggleClient = (clientId) => {
    setSelectedClients((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = (clients) => {
    const clientIds = clients?.data?.map((c) => c._id) || [];
    if (selectedClients.length === clientIds.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clientIds);
    }
  };

  const handleAssignClients = () => {
    if (!selectedSupportId || selectedClients.length === 0) return;

    assignClients(
      { supportId: selectedSupportId, clientIds: selectedClients },
      {
        onSuccess: () => {
          setSelectedClients([]);
          setIsAssignDialogOpen(false);
        },
      }
    );
  };

  const handleUnassignClients = () => {
    if (!selectedSupportId || selectedClients.length === 0) return;

    unassignClients(
      { supportId: selectedSupportId, clientIds: selectedClients },
      {
        onSuccess: () => {
          setSelectedClients([]);
        },
      }
    );
  };

  // Handle nested data structure from API response
  const supportUsersData = supportUsers?.data?.data || supportUsers?.data || [];
  const assignedClientsData = assignedClients?.data?.data || assignedClients?.data || [];
  const unassignedClientsData = unassignedClients?.data?.data || unassignedClients?.data || [];

  const selectedSupport = supportUsersData.find(
    (s) => s._id === selectedSupportId
  );

  return (
    <>
      <HeadLayout>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">
                {t('clientSupport.management', 'Client Support Management')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('clientSupport.managementDesc', 'Assign and manage clients for support agents')}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetchSupportUsers();
              refetchAssignedClients();
              refetchUnassignedClients();
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common.refresh', 'Refresh')}
          </Button>
        </div>
      </HeadLayout>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
        {/* Support Users List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t('clientSupport.supportAgents', 'Support Agents')}
            </CardTitle>
            <CardDescription>
              {t('clientSupport.selectAgent', 'Select an agent to manage their clients')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              {isLoadingSupportUsers ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : supportUsersData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('clientSupport.noAgents', 'No support agents found')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {supportUsersData.map((support) => (
                    <Card
                      key={support._id}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedSupportId === support._id
                          ? 'border-primary ring-2 ring-primary/20'
                          : ''
                      }`}
                      onClick={() => handleSelectSupport(support._id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">{support.fullName}</h4>
                            <p className="text-sm text-muted-foreground">
                              {support.email}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">
                              {support.assignedClientsCount || 0}
                            </Badge>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Assigned Clients */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  {t('clientSupport.assignedClients', 'Assigned Clients')}
                </CardTitle>
                {selectedSupport && (
                  <CardDescription>
                    {selectedSupport.fullName}
                  </CardDescription>
                )}
              </div>
              {selectedSupportId && assignedClientsData.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSelectAll(assignedClients)}
                  >
                    {selectedClients.length === assignedClientsData.length ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </Button>
                  {selectedClients.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleUnassignClients}
                      disabled={isUnassigning}
                    >
                      <UserMinus className="w-4 h-4 mr-2" />
                      {t('clientSupport.unassign', 'Unassign')} ({selectedClients.length})
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              {!selectedSupportId ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('clientSupport.selectAgentFirst', 'Select a support agent first')}</p>
                </div>
              ) : isLoadingAssignedClients ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : assignedClientsData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('clientSupport.noAssignedClients', 'No clients assigned yet')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {assignedClientsData.map((client) => (
                    <Card key={client._id} className="hover:shadow-sm transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedClients.includes(client._id)}
                            onCheckedChange={() => handleToggleClient(client._id)}
                          />
                          <div className="flex-1">
                            <h5 className="font-medium">{client.fullName}</h5>
                            <p className="text-xs text-muted-foreground">
                              {client.brandName} • {client.email}
                            </p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                📦 {client.packagesCount || 0}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                🧾 {client.invoicesCount || 0}
                              </Badge>
                              {client.isActive ? (
                                <Badge variant="success" className="text-xs">
                                  {t('common.active', 'Active')}
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">
                                  {t('common.inactive', 'Inactive')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Unassigned Clients */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-600" />
                  {t('clientSupport.unassignedClients', 'Unassigned Clients')}
                </CardTitle>
                <CardDescription>
                  {t('clientSupport.unassignedDesc', 'Clients without support agent')}
                </CardDescription>
              </div>
              {selectedSupportId && (
                <Button
                  size="sm"
                  onClick={() => setIsAssignDialogOpen(true)}
                  disabled={!selectedSupportId}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t('clientSupport.assignNew', 'Assign')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t('common.search', 'Search...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <ScrollArea className="h-[530px]">
              {isLoadingUnassignedClients ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : unassignedClientsData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('clientSupport.noUnassignedClients', 'All clients are assigned')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {unassignedClientsData
                    .filter(
                      (client) =>
                        client.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        client.brandName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        client.email?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((client) => (
                      <Card key={client._id} className="hover:shadow-sm transition-shadow">
                        <CardContent className="p-3">
                          <div className="flex-1">
                            <h5 className="font-medium">{client.fullName}</h5>
                            <p className="text-xs text-muted-foreground">
                              {client.brandName} • {client.email}
                            </p>
                            <div className="flex gap-2 mt-1">
                              {client.isActive ? (
                                <Badge variant="success" className="text-xs">
                                  {t('common.active', 'Active')}
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">
                                  {t('common.inactive', 'Inactive')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Assign Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('clientSupport.assignClientsTo', 'Assign Clients to')} {selectedSupport?.fullName}
            </DialogTitle>
            <DialogDescription>
              {t('clientSupport.selectClientsToAssign', 'Select clients to assign to this support agent')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Input
                placeholder={t('common.search', 'Search...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm"
              />
              <Badge variant="secondary">
                {selectedClients.length} {t('common.selected', 'selected')}
              </Badge>
            </div>
            <Separator />
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {unassignedClientsData
                  .filter(
                    (client) =>
                      client.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      client.brandName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      client.email?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((client) => (
                    <Card key={client._id}>
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedClients.includes(client._id)}
                            onCheckedChange={() => handleToggleClient(client._id)}
                          />
                          <div className="flex-1">
                            <h5 className="font-medium">{client.fullName}</h5>
                            <p className="text-xs text-muted-foreground">
                              {client.brandName} • {client.email}
                            </p>
                          </div>
                          {client.isActive ? (
                            <Badge variant="success" className="text-xs">
                              {t('common.active', 'Active')}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">
                              {t('common.inactive', 'Inactive')}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </ScrollArea>
            <Separator />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleAssignClients}
                disabled={selectedClients.length === 0 || isAssigning}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {t('clientSupport.assignClients', 'Assign Clients')} ({selectedClients.length})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientSupportManagement;
